import { UserHelper } from '@/helpers/user.helper';
import { AlertService } from '@/services/alert.service';
import { AppConfigService } from '@/services/app-config.service';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormBuilder, UntypedFormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { ContactMessage, MarketPlaceService, RealProperty } from '@eemo/api-client';
import { Subscription } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { NgTemplateOutlet, NgIf } from '@angular/common';
import { ModalBaseComponent } from '../base/modal-base/modal-base.component';

export interface ContactDialogData {
  type: 'referent' | 'notary' | 'propertyService';
  prop: RealProperty;
  targetName: string;
  targetEmail: string;
}

@Component({
    selector: 'app-contact-dialog',
    templateUrl: './contact-dialog.component.html',
    styleUrls: ['./contact-dialog.component.scss'],
    standalone: true,
    imports: [ModalBaseComponent, NgTemplateOutlet, MatDialogModule, FormsModule, ReactiveFormsModule, MatFormFieldModule, MatInputModule, NgIf]
})
export class ContactDialogComponent implements OnInit, OnDestroy {
  contactForm: UntypedFormGroup;
  isLoading = false;
  dialogTitle: string;
  sub = new Subscription();
  canSend = false;

  constructor(
    public dialogRef: MatDialogRef<ContactDialogComponent>,
    private marketPlaceService: MarketPlaceService,
    private fb: UntypedFormBuilder,
    private appConfigService: AppConfigService,
    private userHelper: UserHelper,
    private alertService: AlertService,
    @Inject(MAT_DIALOG_DATA) public data: ContactDialogData
  ) {
    this.dialogTitle = 'Contacter ' + this.data.targetName;
    const prop = this.appConfigService.selectedProp;
    this.canSend = this.userHelper.canUpdateProperty(prop);
  }

  // convenience getter for easy access to form fields
  get form_message() {
    return this.contactForm.get('message') as FormControl<string>;
  }
  f(field: string): AbstractControl {
    return this.contactForm.get(field);
  }

  ngOnInit() {
    this.contactForm = this.fb.group({
      message: [null, [Validators.maxLength(255), Validators.required]]
    });
    if (!this.canSend) {
      this.contactForm.disable();
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  onSubmit() {
    if (this.contactForm.valid) {
      if (this.data.type === 'referent') {
        this.sendMessageToReferent();
      } else if (this.data.type === 'notary') {
        this.sendMessageToNotary();
      } else if (this.data.type === 'propertyService') {
        this.sendMessageToPropertyService();
      }
    }
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  sendMessageToReferent() {
    if (this.contactForm.valid && this.canSend) {
      const body: ContactMessage = {
        message: this.form_message.value
      };
      this.isLoading = true;
      this.sub = this.marketPlaceService.contactPropertyReferent(this.data.prop.uid, body).subscribe((res) => {
        this.isLoading = false;
        this.dialogRef.close();
        this.alertService.success('alert.success.msgSent');
      });
    }
  }

  sendMessageToNotary() {
    if (this.contactForm.valid && this.canSend) {
      const body: ContactMessage = {
        message: this.form_message.value
      };
      this.isLoading = true;
      this.sub = this.marketPlaceService.contactPropertyNotary(this.data.prop.uid, body).subscribe((res) => {
        this.isLoading = false;
        this.dialogRef.close();
        this.alertService.success('alert.success.msgSent');
      });
    }
  }

  sendMessageToPropertyService() {
    if (this.contactForm.valid && this.canSend) {
      const body: ContactMessage = {
        message: this.form_message.value
      };
      this.isLoading = true;
      this.sub = this.marketPlaceService
        .contactPropertyCompanyService(this.data.prop.uid, this.data.targetEmail, body)
        .subscribe((res) => {
          this.isLoading = false;
          this.dialogRef.close();
          this.alertService.success('alert.success.msgSent');
        });
    }
  }
}
